import {
    AssignmentInd,
    Create,
    ExtensionRounded,
    LibraryBooksOutlined,
    ChatBubbleOutline,
    BuildCircleRounded,
    SmartToyOutlined,
    BugReportOutlined,
    BiotechOutlined,
    PsychologyOutlined,
    TravelExploreOutlined,
    RouteOutlined,
    SubscriptionsOutlined,
    ForwardToInboxOutlined,
    QueryStatsRounded,
    OutboxOutlined,
} from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
    INavigation,
    INavigationGroup,
} from "Layouts/SideNavigation/NavigationStore";
import SideNavigation from "Layouts/SideNavigationNew/SideNavigation";
import { observer } from "mobx-react";
import { AppDomains } from "models/Permission/AppDomains";
import React, { useState } from "react";
import { useLocation } from "react-router";
import { AuthStore } from "stores/AuthStore";
import AgentsSvg from "SvgIcons/ConfigIcons/AgentsSvg";
import BuildingSvg from "SvgIcons/ConfigIcons/BuildingSvg";
import HierarchySvg from "SvgIcons/ConfigIcons/HierarchySvg";
import ModulesSvg from "SvgIcons/ConfigIcons/ModulesSvg";
import RolesSvg from "SvgIcons/ConfigIcons/RolesSvg";
import UsersSvg from "SvgIcons/ConfigIcons/UsersSvg";
import { ThemeColors } from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

interface ISecuredNavigation extends INavigation {
    permissions: string[];
    domain: AppDomains;
}

const orgSetupItems: ISecuredNavigation[] = [
    {
        title: "Org Details",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId`,
        icon: <BuildingSvg />,
        selectedIcon: <BuildingSvg color={ThemeColors.blue} opacity={1} />,
    },
    {
        title: "Modules",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/modules`,
        icon: <ModulesSvg />,
        selectedIcon: <ModulesSvg color={ThemeColors.blue} opacity={1} />,
    },
    {
        title: "Workflows",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/workflows`,
        icon: (
            <AccountTreeIcon
                style={{
                    width: "19px",
                    height: "20px",
                    fill: "#1F1F1F",
                    opacity: 0.5,
                }}
            />
        ),
        selectedIcon: (
            <AccountTreeIcon
                style={{
                    color: ThemeColors.blue,
                    opacity: 1,
                    width: "19px",
                    height: "20px",
                }}
            />
        ),
    },
    {
        title: "Service Hierarchy",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/service-hierarchy`,
        icon: <HierarchySvg />,
        selectedIcon: <HierarchySvg color={ThemeColors.blue} opacity={1} />,
    },
    {
        title: "Contact Types",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/contact-types`,
        icon: <UsersSvg />,
        selectedIcon: <UsersSvg color={ThemeColors.blue} opacity={1} />,
    },
    {
        title: "Model Controls",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/model-controls`,
        icon: <SettingsSuggestIcon style={{ opacity: 0.5 }} />,
        selectedIcon: <SettingsSuggestIcon color={"primary"} />,
    },
    {
        title: "Data Sources",
        permissions: ["Data Sources"],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/datasources`,
        icon: (
            <ExtensionRounded
                style={{ opacity: 0.5, transform: "scaleX(-1)" }}
            />
        ),
        selectedIcon: (
            <ExtensionRounded
                color={"primary"}
                style={{ transform: "scaleX(-1)" }}
            />
        ),
    },
    {
        title: "Metadata Management",
        permissions: ["Data Sources"],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/metadata-management`,
        icon: <BuildCircleRounded style={{ opacity: 0.5 }} />,
        selectedIcon: <BuildCircleRounded color="primary" />,
    },
    {
        title: "Proactive Insights",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/proactive-insights`,
        icon: <InsightsRoundedIcon style={{ opacity: 0.5 }} />,
        selectedIcon: <InsightsRoundedIcon color="primary" />,
    },
    {
        title: "Prompt Templates",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/llm-prompt-templates`,
        icon: <LibraryBooksOutlined style={{ opacity: 0.5 }} />,
        selectedIcon: <LibraryBooksOutlined color="primary" />,
    },
    {
        title: "SAML SSO",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/saml-sso`,
        icon: <VpnKeyOutlinedIcon style={{ opacity: 0.5 }} />,
        selectedIcon: <VpnKeyOutlinedIcon color="primary" />,
    },
    {
        title: "Model Feedback",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/model-feedback`,
        icon: <ChatBubbleOutline style={{ opacity: 0.5 }} />,
        selectedIcon: <ChatBubbleOutline color="primary" />,
    },
    {
        title: "AI Models",
        permissions: [],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/ai-models`,
        icon: <SettingsSuggestIcon style={{ opacity: 0.5 }} />,
        selectedIcon: <SettingsSuggestIcon color="primary" />,
    },
    {
        title: "Emissions",
        permissions: ["Emissions Configuration"],
        domain: AppDomains.Administrative,
        link: `/admin/organizations/:organizationId/emissions`,
        icon: <OutboxOutlined style={{ opacity: 0.5 }} />,
        selectedIcon: <OutboxOutlined color="primary" />,
        matchSubpaths: true,
    },
];

type Props = {
    organizationId: string;
};

const OrganizationDetailNavigation: React.FC<Props> = observer(
    ({ organizationId }) => {
        const location = useLocation();
        const authStore = useStore(AuthStore);
        const permStore = authStore.permStore;
        const [currentNavItem, setCurrentNavItem] = useState<string>("");

        const managementItems: ISecuredNavigation[] = React.useMemo(
            () => [
                {
                    title: "Roles",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/roles`,
                    icon: <RolesSvg />,
                    selectedIcon: (
                        <RolesSvg color={ThemeColors.blue} opacity={1} />
                    ),
                },
                {
                    title: "Users",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/users`,
                    icon: <UsersSvg />,
                    selectedIcon: (
                        <UsersSvg color={ThemeColors.blue} opacity={1} />
                    ),
                    matchSubpaths: true,
                },
                {
                    title: "Agents",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/agents`,
                    icon: <AgentsSvg />,
                    selectedIcon: (
                        <AgentsSvg color={ThemeColors.blue} opacity={1} />
                    ),
                },
                {
                    title: "Usage & Value",
                    permissions: ["Usage & Value"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/usage-value`,
                    icon: <QueryStatsRounded style={{ opacity: 0.5 }} />,
                    selectedIcon: <QueryStatsRounded color="primary" />,
                },
            ],
            [],
        );

        const kboItems: ISecuredNavigation[] = React.useMemo(
            () => [
                {
                    title: "KBo",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/kbo-settings`,
                    icon: <SmartToyOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <SmartToyOutlined color="primary" />,
                },
                {
                    title: "Client Brain",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/client-brain`,
                    icon: <PsychologyOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <PsychologyOutlined color="primary" />,
                },
                {
                    title: "Research Findings",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/research-findings`,
                    icon: <TravelExploreOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <TravelExploreOutlined color="primary" />,
                },
                {
                    title: "Agent Skills",
                    permissions: ["Agent Skills"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/agent-skills`,
                    icon: <LibraryBooksOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <LibraryBooksOutlined color="primary" />,
                },
                {
                    title: "Chat Review",
                    permissions: ["KBo Chat Data"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/chat-review`,
                    icon: <ChatBubbleOutline style={{ opacity: 0.5 }} />,
                    selectedIcon: <ChatBubbleOutline color="primary" />,
                    enabled: authStore.canUserView("KBo Chat Data"),
                    matchSubpaths: true,
                },
                {
                    title: "Subscriptions",
                    permissions: ["Client Brain"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/kbo-flow-subscriptions`,
                    icon: <SubscriptionsOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <SubscriptionsOutlined color="primary" />,
                },
                {
                    title: "KBo Flow Runs",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/kbo-flow-runs`,
                    icon: <RouteOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <RouteOutlined color="primary" />,
                    matchSubpaths: true,
                },
                {
                    title: "Send Log",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/send-log`,
                    icon: <ForwardToInboxOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <ForwardToInboxOutlined color="primary" />,
                },
                {
                    title: "Kbo Flow Debug",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/kbo-flow-debug`,
                    icon: <BugReportOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <BugReportOutlined color="primary" />,
                },
                {
                    title: "Flow Step Debug",
                    permissions: [],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/flow-step-debug`,
                    icon: <BiotechOutlined style={{ opacity: 0.5 }} />,
                    selectedIcon: <BiotechOutlined color="primary" />,
                },
            ],
            [authStore],
        );

        const MotfItems: ISecuredNavigation[] = React.useMemo(
            () => [
                {
                    title: "Definitions",
                    permissions: ["Sample Automation"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/motf/definitions`,
                    icon: <AssignmentInd />,
                    enabled: authStore.canUserView("Sample Automation"),
                    selectedIcon: (
                        <AssignmentInd
                            style={{ color: ThemeColors.blue }}
                            opacity={1}
                        />
                    ),
                    matchSubpaths: true,
                },
                {
                    title: "Create Definition",
                    permissions: ["Sample Automation"],
                    domain: AppDomains.Administrative,
                    link: `/admin/organizations/:organizationId/motf/create`,
                    icon: <Create />,
                    enabled: authStore.canUserEdit("Sample Automation"),
                    selectedIcon: (
                        <Create
                            style={{ color: ThemeColors.blue }}
                            opacity={1}
                        />
                    ),
                    matchSubpaths: true,
                },
            ],
            [authStore],
        );

        const [navGroupItems, setNavGroupItems] = useState<INavigationGroup[]>([
            { header: "Management", items: managementItems },
            { header: "Organization Setup", items: orgSetupItems },
            { header: "KBo", items: kboItems },
            { header: "Sample Automation", items: MotfItems },
        ]);

        // Hide Workflows tab and Sample Automation expansion if permission not enabled
        React.useEffect(() => {
            if (!authStore.canUserEdit("Workflow Conditions")) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Workflows",
                        );
                    }
                    return [...prev];
                });
            }

            if (
                !authStore.canUserEdit("Data Sources") &&
                !authStore.canUserView("Metadata Management")
            ) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Metadata Management",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Emissions Configuration")) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Emissions",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Data Sources")) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Data Sources",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Agent Skills")) {
                setNavGroupItems((prev) => {
                    const kboGroup = prev.find((g) => g.header === "KBo");

                    if (kboGroup) {
                        kboGroup.items = kboGroup.items.filter(
                            (item) => item.title !== "Agent Skills",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Usage & Value")) {
                setNavGroupItems((prev) => {
                    const managementGroup = prev.find(
                        (g) => g.header === "Management",
                    );

                    if (managementGroup) {
                        managementGroup.items = managementGroup.items.filter(
                            (item) => item.title !== "Usage & Value",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Client Brain")) {
                setNavGroupItems((prev) => {
                    const kboGroup = prev.find((g) => g.header === "KBo");

                    if (kboGroup) {
                        kboGroup.items = kboGroup.items.filter(
                            (item) => item.title !== "Subscriptions",
                        );
                    }
                    return [...prev];
                });
            }

            if (!authStore.canUserView("Sample Automation"))
                setNavGroupItems((oldItems) =>
                    oldItems.filter(
                        (item) => item.header !== "Sample Automation",
                    ),
                );

            if (!authStore.canUserEdit("Organization Contact Types")) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Contact Types",
                        );
                    }
                    return [...prev];
                });
            }

            if (
                !authStore.canUserEdit("Model Controls") &&
                !authStore.canUserView("Model Controls")
            )
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "Model Controls",
                        );
                    }
                    return [...prev];
                });

            if (!authStore.canUserEdit("Organization Details")) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) => item.title !== "SAML SSO",
                        );
                    }
                    return [...prev];
                });
            }

            // Hiding ultra-only org setup items for non-ultra users
            const ultraOnlyOrgSetupItems = [
                "Prompt Templates",
                "Proactive Insights",
                "AI Models",
                "Model Feedback",
            ];
            if (!authStore.isUserUltra()) {
                setNavGroupItems((prev) => {
                    const orgSetupGroup = prev.find(
                        (g) => g.header === "Organization Setup",
                    );

                    if (orgSetupGroup) {
                        orgSetupGroup.items = orgSetupGroup.items.filter(
                            (item) =>
                                !ultraOnlyOrgSetupItems.includes(
                                    item.title as string,
                                ),
                        );
                    }
                    return [...prev];
                });
            }

            // KBo section: hide it entirely when the user has access to none of
            // its pages; otherwise hide the ultra-only items within it.
            if (
                !authStore.isUserUltra() &&
                !authStore.canUserView("Agent Skills") &&
                !authStore.canUserView("KBo Chat Data") &&
                !authStore.canUserView("Client Brain")
            ) {
                setNavGroupItems((oldItems) =>
                    oldItems.filter((item) => item.header !== "KBo"),
                );
            } else if (!authStore.isUserUltra()) {
                const ultraOnlyKBoItems = [
                    "KBo",
                    "Client Brain",
                    "KBo Flow Runs",
                    "Send Log",
                    "Kbo Flow Debug",
                    "Flow Step Debug",
                    "Research Findings",
                ];
                setNavGroupItems((prev) => {
                    const kboGroup = prev.find((g) => g.header === "KBo");

                    if (kboGroup) {
                        kboGroup.items = kboGroup.items.filter(
                            (item) =>
                                !ultraOnlyKBoItems.includes(
                                    item.title as string,
                                ),
                        );
                    }
                    return [...prev];
                });
            }
        }, [permStore, authStore, organizationId, MotfItems, kboItems]);

        React.useEffect(() => {
            // Max depth is how many segments of the path we want to consider for matching
            // This is useful to avoid matching too deeply nested routes
            // The deepest route in Admin is /admin/organizations/44cc8a0c-ad93-40e6-b8c4-5410eacba3b0/motf/definitions
            // 5 segments deep (and splice is exclusive of the last element so we add 1 to get 6)
            const maxDepth = 6;
            const path = location.pathname;
            const pathSplit = path.split("/");
            const currentItem = pathSplit.slice(0, maxDepth).join("/");
            setCurrentNavItem(currentItem);
        }, [location.pathname]);

        return (
            <SideNavigation
                items={navGroupItems}
                currentNavItem={currentNavItem}
                organizationId={organizationId}
            />
        );
    },
);

export default OrganizationDetailNavigation;
